// =============================================================================
// debugErrorLog.js — ADMIN app. On-screen debug error overlay + Neon log.
//
// Patrick 2026-06-28: the admin app shows a bare "failed to load / network error"
// with no detail. This makes the ACTUAL error visible right on the screen, and
// also writes it to Neon (analytics.client_errors, source='admin') so we
// can see it server-side too.
//
// The overlay is VANILLA DOM (not React) on purpose: it must still appear when
// the React tree has crashed to a white screen — which is exactly the case
// Patrick hit. Everything here is best-effort and never throws.
// =============================================================================
import React from 'react';
import { API_BASE_URL } from './constants';

const SOURCE = 'admin';
const APP_VERSION = (typeof window !== 'undefined' && window.__HL_ADMIN_BUILD__) || 'admin-dev';
const MAX_CARDS = 6;

// ── on-screen overlay ────────────────────────────────────────────────────────
function ensureContainer() {
  let c = document.getElementById('hl-debug-overlay');
  if (c) return c;
  c = document.createElement('div');
  c.id = 'hl-debug-overlay';
  c.style.cssText = [
    'position:fixed', 'left:0', 'right:0', 'bottom:0', 'z-index:2147483647',
    'display:flex', 'flex-direction:column', 'gap:8px', 'padding:8px',
    'max-height:60vh', 'overflow-y:auto', 'pointer-events:none',
    'font-family:-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif',
  ].join(';');
  (document.body || document.documentElement).appendChild(c);
  return c;
}

function fmtTime() {
  try { return new Date().toLocaleTimeString(); } catch { return ''; }
}

/** Show a dismissible red error card on screen. */
export function showErrorOverlay(title, detail, stack) {
  try {
    const c = ensureContainer();
    // cap the number of visible cards
    while (c.children.length >= MAX_CARDS) c.removeChild(c.firstChild);

    const card = document.createElement('div');
    card.style.cssText = [
      'pointer-events:auto', 'background:#7f1d1d', 'color:#fff',
      'border:1px solid #ef4444', 'border-radius:10px', 'padding:10px 12px',
      'box-shadow:0 4px 16px rgba(0,0,0,0.35)', 'font-size:13px', 'line-height:1.4',
    ].join(';');

    const head = document.createElement('div');
    head.style.cssText = 'display:flex;align-items:flex-start;gap:8px;';

    const txt = document.createElement('div');
    txt.style.cssText = 'flex:1;min-width:0;word-break:break-word;';
    const t = document.createElement('div');
    t.style.cssText = 'font-weight:800;';
    t.textContent = '⚠️ ' + (title || 'Error');
    const d = document.createElement('div');
    d.style.cssText = 'opacity:0.85;font-size:11px;margin-top:2px;';
    d.textContent = [detail, fmtTime()].filter(Boolean).join('  ·  ');
    txt.appendChild(t); txt.appendChild(d);

    if (stack) {
      const pre = document.createElement('pre');
      pre.style.cssText = 'display:none;white-space:pre-wrap;font-size:10px;margin:8px 0 0;max-height:30vh;overflow:auto;opacity:0.8;';
      pre.textContent = String(stack);
      const more = document.createElement('button');
      more.textContent = 'stack ▾';
      more.style.cssText = 'background:none;border:none;color:#fecaca;font-size:11px;cursor:pointer;padding:2px 0;text-decoration:underline;';
      more.onclick = () => { pre.style.display = pre.style.display === 'none' ? 'block' : 'none'; };
      txt.appendChild(more); txt.appendChild(pre);
    }

    const x = document.createElement('button');
    x.textContent = '✕';
    x.style.cssText = 'background:none;border:none;color:#fff;font-size:16px;cursor:pointer;flex-shrink:0;line-height:1;padding:0 2px;';
    x.onclick = () => { try { c.removeChild(card); } catch {} };

    head.appendChild(txt); head.appendChild(x);
    card.appendChild(head);
    c.appendChild(card);
  } catch { /* overlay must never throw */ }
}

// ── Neon logging ─────────────────────────────────────────────────────────────
function isAuthed() { try { return !!localStorage.getItem('token'); } catch { return false; } }
function currentUserId() { try { return localStorage.getItem('user_id') || null; } catch { return null; } }
function regionGuess() { try { return localStorage.getItem('user_region') || 'th'; } catch { return 'th'; } }

/** Report an error: show it on screen AND POST it to Neon. */
export function reportClientError(opts = {}) {
  const message = opts.message || opts.error_message || 'Unknown error';
  const detailBits = [];
  if (opts.endpoint) detailBits.push(opts.endpoint);
  if (opts.status) detailBits.push('HTTP ' + opts.status);
  if (opts.kind) detailBits.push(opts.kind);
  showErrorOverlay(message, detailBits.join('  ·  '), opts.stack || opts.error_stack);

  try {
    const body = {
      source: SOURCE,
      kind: opts.kind || 'error',
      url: (() => { try { return window.location.href; } catch { return null; } })(),
      route: (() => { try { return window.location.pathname + window.location.search; } catch { return null; } })(),
      error_name: opts.name || opts.error_name || null,
      error_message: String(message).slice(0, 2000),
      error_stack: opts.stack || opts.error_stack || null,
      component_stack: opts.componentStack || null,
      user_id: currentUserId(),
      is_auth: isAuthed(),
      region: regionGuess(),
      app_version: APP_VERSION,
      extra: {
        endpoint: opts.endpoint || null,
        method: opts.method || null,
        status: opts.status || null,
        ...(opts.extra || {}),
      },
    };
    const url = `${API_BASE_URL}/client-errors`;
    const json = JSON.stringify(body);
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([json], { type: 'application/json' }));
    } else {
      fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: json, keepalive: true }).catch(() => {});
    }
  } catch { /* logging must never cascade */ }
}

// ── global handlers (uncaught errors + promise rejections) ───────────────────
let installed = false;
export function installGlobalErrorHandlers() {
  if (installed) return;
  installed = true;
  try {
    window.addEventListener('error', (e) => {
      // ignore benign resource-load 'error' events without an Error object only
      if (!e.message && !e.error) return;
      reportClientError({ kind: 'window', name: e.error?.name, message: e.message || 'Script error', stack: e.error?.stack });
    });
    window.addEventListener('unhandledrejection', (e) => {
      const r = e.reason;
      reportClientError({ kind: 'promise', name: r?.name, message: r?.message || String(r) || 'Unhandled promise rejection', stack: r?.stack });
    });
  } catch { /* ignore */ }
}

// ── React error boundary (render-time crashes) ───────────────────────────────
export class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) {
    reportClientError({
      kind: 'react', name: error?.name, message: error?.message || 'Render crash',
      stack: error?.stack, componentStack: info?.componentStack,
    });
  }
  render() {
    if (this.state.error) {
      return React.createElement('div', {
        style: { padding: 24, fontFamily: 'sans-serif', color: '#7f1d1d' },
      }, [
        React.createElement('div', { key: 't', style: { fontSize: 18, fontWeight: 800, marginBottom: 8 } }, '⚠️ Something broke'),
        React.createElement('div', { key: 'm', style: { fontSize: 14, marginBottom: 12, wordBreak: 'break-word' } }, String(this.state.error?.message || this.state.error)),
        React.createElement('button', {
          key: 'r',
          onClick: () => { try { window.location.reload(); } catch {} },
          style: { padding: '10px 20px', borderRadius: 8, border: 'none', background: '#E46E34', color: '#fff', fontWeight: 700, cursor: 'pointer' },
        }, 'Reload'),
      ]);
    }
    return this.props.children;
  }
}
